import dayjs from "dayjs";

/** Format a decimal (weight or return, e.g. 0.12) as a percentage; negatives as "(x)%". */
const formatDecimalAsPercent = (value, decimals = 4) => {
  if (value === null || value === undefined) return "-";
  const num = Number(value) * 100;
  const pct = Math.abs(num).toFixed(decimals);
  return value >= 0 ? `${pct}%` : `(${pct}%)`;
};

export const formatWeight = formatDecimalAsPercent;
export const formatReturns = formatDecimalAsPercent;

/**
 * Return theme color for returns: green positive, red negative, default text otherwise.
 */
export const getReturnsColor = (value, theme) => {
  if (value === null || value === undefined) return theme.palette.text.primary;
  if (value > 0) return theme.palette.success?.main ?? "#2e7d32";
  if (value < 0) return theme.palette.error?.main ?? "#d32f2f";
  return theme.palette.text.primary;
};

/** Build table rows from marketAdjustedWeights payload. */
export const buildHoldingsRows = (marketAdjustedWeights) => {
  if (!marketAdjustedWeights) return [];
  const keys = marketAdjustedWeights.mkt_adj_weights_keys || {};
  const originalWeights = marketAdjustedWeights.original_weights || {};
  const mktAdjValues = marketAdjustedWeights.mkt_adj_weights_values || {};
  const stockReturns = marketAdjustedWeights.stock_returns || {};
  const rows = [];
  Object.keys(keys).forEach((id) => {
    const meta = keys[id];
    const symbol = meta?.symbol || id;
    const signalWeight = originalWeights[id];
    const mktAdjWeight = mktAdjValues[id];
    const returns = stockReturns[id];
    if (signalWeight === undefined && mktAdjWeight === undefined) return;
    const weight = signalWeight ?? mktAdjWeight ?? 0;
    rows.push({
      id,
      symbol,
      description: "-",
      side: weight >= 0 ? "Long" : "Short",
      signalWeight: signalWeight ?? 0,
      mktAdjWeight: mktAdjWeight ?? 0,
      returns: returns ?? 0,
    });
  });
  return rows;
};

/** Parse `YYYY-MM-DD` or ISO date prefix into a dayjs instance, or `null` if invalid. */
export const parseYmdBoundary = (ymd) => {
  if (!ymd || typeof ymd !== "string") return null;
  const d = dayjs(ymd.split("T")[0]);
  return d.isValid() ? d : null;
};

/**
 * Min and max ISO calendar dates from a list. Lexicographic order matches chronological for `YYYY-MM-DD`.
 * @returns {{ min: string, max: string }} Empty strings when the list is empty.
 */
export const minMaxYmdFromList = (ymds) => {
  if (!Array.isArray(ymds) || ymds.length === 0) return { min: "", max: "" };
  let min = String(ymds[0]);
  let max = min;
  for (let i = 1; i < ymds.length; i++) {
    const y = String(ymds[i]);
    if (y < min) min = y;
    if (y > max) max = y;
  }
  return { min, max };
};

/** e.g. 1 → "1st", 2 → "2nd", 3 → "3rd", 11 → "11th" */
export const englishOrdinal = (n) => {
  const k = Math.abs(Math.floor(Number(n)));
  if (!Number.isFinite(k) || k < 0) return String(n);
  const cent = k % 100;
  if (cent >= 11 && cent <= 13) return `${k}th`;
  switch (k % 10) {
    case 1:
      return `${k}st`;
    case 2:
      return `${k}nd`;
    case 3:
      return `${k}rd`;
    default:
      return `${k}th`;
  }
}